import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiX, FiCheck, FiMinus } from 'react-icons/fi';
import { useCompare } from '../context/CompareContext';

const ComparePage = () => {
  const { compareList, removeFromCompare, clearCompare } = useCompare();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getRtoMonthly = (shed) => {
    const base = shed.salePrice && shed.salePrice < shed.price ? shed.salePrice : shed.price;
    return formatPrice(Math.round((base * 1.45) / 36));
  };

  const rows = [
    { label: 'Price', render: (s) => s.salePrice && s.salePrice < s.price ? (
      <div>
        <span className="text-sm text-gray-400 line-through block">{formatPrice(s.price)}</span>
        <span className="text-lg font-semibold text-red-600">{formatPrice(s.salePrice)}</span>
      </div>
    ) : <span className="text-lg font-semibold text-gray-900">{formatPrice(s.price)}</span> },
    { label: 'RTO Monthly', render: (s) => <span className="text-sm font-medium text-gray-700">{getRtoMonthly(s)}/mo</span> },
    { label: 'Size', render: (s) => s.size },
    { label: 'Square Feet', render: (s) => `${s.squareFeet} sq ft` },
    { label: 'Height', render: (s) => s.height ? `${s.height} ft` : '—' },
    { label: 'Style', render: (s) => <span className="capitalize">{s.shedType}</span> },
    { label: 'Material', render: (s) => s.material || '—' },
    { label: 'Body Color', render: (s) => s.bodyColor || s.sidingColor || '—' },
    { label: 'Roof Color', render: (s) => s.roofColor || '—' },
    { label: 'Trim Color', render: (s) => s.trimColor || '—' },
    { label: 'Roof Style', render: (s) => s.roofStyle || '—' },
    { label: 'Siding', render: (s) => s.sidingType || '—' },
    { label: 'Loft', render: (s) => s.loft ? <FiCheck className="w-4 h-4 text-green-600" /> : <FiMinus className="w-4 h-4 text-gray-300" /> },
    { label: 'Electrical', render: (s) => s.electrical ? <FiCheck className="w-4 h-4 text-green-600" /> : <FiMinus className="w-4 h-4 text-gray-300" /> },
    { label: 'Insulation', render: (s) => s.insulation ? <FiCheck className="w-4 h-4 text-green-600" /> : <FiMinus className="w-4 h-4 text-gray-300" /> },
    { label: 'Workbench', render: (s) => s.workbench ? <FiCheck className="w-4 h-4 text-green-600" /> : <FiMinus className="w-4 h-4 text-gray-300" /> },
    { label: 'Free Delivery', render: (s) => s.deliveryIncluded ? <FiCheck className="w-4 h-4 text-green-600" /> : <FiMinus className="w-4 h-4 text-gray-300" /> },
    { label: 'Free Setup', render: (s) => s.setupIncluded ? <FiCheck className="w-4 h-4 text-green-600" /> : <FiMinus className="w-4 h-4 text-gray-300" /> },
    { label: 'Warranty', render: (s) => s.warranty || '—' },
    { label: 'Dealer', render: (s) => s.dealerName || '—' },
    { label: 'Location', render: (s) => s.dealerLocation || '—' },
    { label: 'Built By', render: (s) => s.brand || '—' },
  ];

  if (compareList.length === 0) {
    return (
      <>
        <Helmet>
          <title>Compare Sheds | Side-by-Side Comparison</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50 pt-24 pb-16">
          <div className="container mx-auto px-4 text-center py-20">
            <h1 className="text-2xl font-semibold text-gray-900 mb-3">Compare Sheds</h1>
            <p className="text-gray-500 mb-6">Add up to 3 sheds from the inventory to compare them side by side.</p>
            <Link to="/sheds" className="inline-block bg-gray-900 text-white text-sm font-medium px-6 py-3 hover:bg-gray-800 transition-colors">
              Browse Inventory
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Compare Sheds | Side-by-Side Comparison</title>
        <meta name="description" content="Compare sheds side by side — pricing, dimensions, features and specifications at a glance." />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Compare Sheds</h1>
              <p className="text-sm text-gray-500 mt-1">{compareList.length} of 3 sheds selected</p>
            </div>
            <div className="flex gap-3">
              <Link to="/sheds" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                + Add more
              </Link>
              <button onClick={clearCompare} className="text-sm text-red-500 hover:text-red-700 transition-colors">
                Clear all
              </button>
            </div>
          </div>

          <div className="bg-white border border-gray-200 overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="w-40 p-4 text-left text-xs text-gray-400 uppercase tracking-wider font-medium">Spec</th>
                  {compareList.map(shed => (
                    <th key={shed.id} className="p-4 text-left" style={{ width: `${60 / compareList.length}%` }}>
                      <div className="relative">
                        <button
                          onClick={() => removeFromCompare(shed.id)}
                          className="absolute -top-1 -right-1 p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                        <Link to={`/shed/${shed.id}`} className="block group">
                          <div className="aspect-[4/3] bg-gray-100 mb-2 overflow-hidden">
                            <img
                              src={shed.images?.[0] || '/assets/images/placeholder-shed.jpg'}
                              alt={shed.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-sm font-medium text-gray-900 group-hover:underline">{shed.name}</p>
                        </Link>
                      </div>
                    </th>
                  ))}
                  {compareList.length < 3 && (
                    <th className="p-4" style={{ width: `${60 / 3}%` }}>
                      <Link to="/sheds" className="block border-2 border-dashed border-gray-200 aspect-[4/3] flex items-center justify-center text-sm text-gray-400 hover:border-gray-300 hover:text-gray-600 transition-colors mb-2">
                        + Add shed
                      </Link>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'}>
                    <td className="p-4 text-xs text-gray-400 uppercase tracking-wider font-medium">{row.label}</td>
                    {compareList.map(shed => (
                      <td key={shed.id} className="p-4 text-sm text-gray-700">
                        {row.render(shed)}
                      </td>
                    ))}
                    {compareList.length < 3 && <td className="p-4" />}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default ComparePage;
